import { Component, ChangeDetectionStrategy, signal } from "@angular/core";
import { Router } from "@angular/router";
import { FormsModule } from "@angular/forms";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { ActivatedRoute } from "@angular/router";
import { AbstractBuyerComponent } from "./abstract/buyer.component";
import { Pair } from "../obj/Pair";
import { AuthService } from "../service/auth.service";
import { AnalyticsService } from "../service/analytics.service";
import { GeneralService } from "../service/general.service";
import { ContractStatus } from "../obj/ContractStatus";
import { Contract } from "../entity/Contract";
import { ExistingContract } from "../entity/ExistingContract";
import { KilowantContract } from "../entity/KilowantContract";
import { ConfirmationDialog } from "./incs/confirmation-dialog.component";
import { MatTabChangeEvent, MatTabGroup, MatTab } from '@angular/material/tabs';
import { MatSort, Sort, MatSortHeader } from "@angular/material/sort";
import { MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow } from '@angular/material/table';
import { HeaderComponent } from "./incs/header.component";
import { CurrencyPipe, DatePipe } from "@angular/common";
import { Company } from "../entity/Company";
import { firstValueFrom } from "rxjs";
import { Account } from "../entity/Account";
import { AccountSelector } from "../obj/AccountSelector";

@Component({
    selector: "kilowant-contract-list",
    templateUrl: "../../html/main-contract-list.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [HeaderComponent, FormsModule, MatTabGroup, MatTab, MatTable, MatSort, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatSortHeader, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, CurrencyPipe, DatePipe]
})
export class ContractListComponent extends AbstractBuyerComponent {
    dialogRef: MatDialogRef<ConfirmationDialog> = null!;
    existingContracts: ExistingContract[] = null!;
    pendingContracts: Pair[] = null!;
    pendingContractsSorted: Pair[] = null!;
    viewPendingContractsAccounts: boolean[] = [];
    openContracts: Pair[] = null!;
    openContractsSorted: Pair[] = null!;
    viewOpenContractsAccounts: boolean[] = [];
    closedContracts: Pair[] = null!;
    closedContractsSorted: Pair[] = null!;
    viewClosedContractsAccounts: boolean[] = [];
    tabIndex: number = 0;
    currentStart: number = 0;
    displayedColumns: string[] = ["id", "Price", "ServiceStart", "ServiceEnd", "DateEntered"];
    totalPending: number = 0;
    totalOpen: number = 0;
    totalClosed: number = 0;
    editModePos: number = -1;
    hasClosedContracts: boolean = false;

    companyFilter = signal<boolean>(false);
    selectedFilterCompany: Company | string | null = "all";
    userCompanies: (Company|null)[];

    selectedFilterAccount: AccountSelector | null = null;
    userAccounts: AccountSelector[];

    constructor(
        router: Router,
        route: ActivatedRoute,
        authService: AuthService,
        analyticsService: AnalyticsService,
        generalService: GeneralService,
        dialog: MatDialog
    ) { super(router, route, dialog, authService, analyticsService, generalService); }

    ngOnInit(): void {
        this.analyticsService.trackEvent("Price Plans Listing Page", "User Opens Price Plans Listing Page", "OPEN_PAGE");
        this.route.fragment.subscribe((fragment: string | null) => {
            if (fragment == 'open') {
                this.tabIndex = 1;
                this.refreshView();
            }
            if (fragment == 'closed') {
                this.tabIndex = 2;
                this.refreshView();
            }
        });

        this.eventSubscriptions.push(this.generalService.changeClientActivities$.subscribe(
            client => {
                this.pageStart();
            })
        );

        super.ngOnInit();
    }

    pageStart(): void {
        const userFilter = this.generalService.getStoredUserFilter();
        if (userFilter && !this.user.isAdvisor) {
            this.companyFilter.set(true);
            this.selectedFilterCompany = userFilter.value;
            firstValueFrom(this.generalService.getUsersActiveCompanies()).then(companies => {
                this.userCompanies = companies;
                this.generalService.getAccountsPlusCurrentCost(userFilter).then(accounts => {
                    this.userAccounts = accounts;
                    this.reloadKilowantContracts();
                });
            });
        } else {
            this.generalService.getAccountsPlusCurrentCost(null).then(accounts => {
                this.userAccounts = accounts;
                this.reloadKilowantContracts();
            });
        }
    }

    reloadFilterAccounts(): void {
        const userFilter = new Pair(this.selectedFilterCompany != null, this.selectedFilterCompany);
        this.generalService.getAccountsPlusCurrentCost(userFilter).then(accounts => {
            this.userAccounts = accounts;
            this.reloadKilowantContracts();
        });
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }

    addExistingContract(): void {
        this.router.navigate(["/priceplans/details"]);
    }

    editExistingContract(existingContract: ExistingContract): void {
        this.router.navigate(["/priceplans/details", existingContract.id]);
    }

    deleteExistingContract(existingContract: ExistingContract): void {
        this.dialogRef = this.dialog.open(ConfirmationDialog, {
            disableClose: false,
            panelClass: "i-icon-popup"
        });
        this.dialogRef.componentInstance.confirmMessage = "Are you sure you want to delete this price plan?"

        this.dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.generalService.deleteExistingContract(existingContract.id)
                    .subscribe((res: any) => {
                        this.reloadKilowantContracts();
                    },
                        err => {
                        });
            }
            this.dialogRef = null!;
            this.refreshView();
        });
    }

    cancelContract(contract: Contract): void {
        this.dialogRef = this.dialog.open(ConfirmationDialog, {
            disableClose: false,
            panelClass: "i-icon-popup"
        });
        this.dialogRef.componentInstance.confirmMessage = "Are you sure you want to cancel this price plan?"

        this.dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.generalService.cancelContract(contract.id)
                    .subscribe((res: any) => {
                        this.reloadKilowantContracts();
                    },
                        err => {
                        });
            }
            this.dialogRef = null!;
            this.refreshView();
        });
    }

    viewContract(contract: Contract, isKilowant: boolean): void {
        if (isKilowant) {
            this.router.navigate(["/view-priceplan", contract.id]);
        } else {
            this.router.navigate(["/view-existing-priceplan", contract.id]);
        }
    }

    viewContractInfo(pos: number): void {
        if (this.editModePos == pos) {
            this.editModePos = -1;
        } else {
            this.editModePos = pos;
        }
        this.refreshView();
    }

    reloadKilowantContracts(): Promise<any> {
        return this.generalService.getContracts((this.selectedFilterAccount == null) ? null : new Account(this.selectedFilterAccount!.id), (this.selectedFilterCompany != "all" && this.companyFilter()), <Company | null>this.selectedFilterCompany, false)
            .then(contracts => {
                this.pendingContracts = [];
                this.openContracts = [];
                this.closedContracts = [];
                for (var i = 0; i < contracts.length; i++) {
                    if ((contracts[i].status == ContractStatus.CLOSED) ||
                        (contracts[i].status == ContractStatus.DECLINED_BY_SELLER) ||
                        (contracts[i].status == ContractStatus.DECLINED_BY_BUYER) ||
                        (contracts[i].status == ContractStatus.DECLINED_VERIFICATION_TIMEOUT)
                    ) {
                        this.closedContracts.push(new Pair(contracts[i] instanceof KilowantContract, contracts[i]));
                        if (!this.isViewContractActive(contracts[i].status)) {
                            this.hasClosedContracts = true;
                        }
                        this.viewClosedContractsAccounts.push(false);
                    } else if (contracts[i].status == ContractStatus.PENDING || contracts[i].status == ContractStatus.PENDING_NON_VERIFIED) {
                        this.pendingContracts.push(new Pair(contracts[i] instanceof KilowantContract, contracts[i]));
                        this.viewPendingContractsAccounts.push(false);
                    } else if (contracts[i].status == ContractStatus.OPEN) {
                        this.openContracts.push(new Pair(contracts[i] instanceof KilowantContract, contracts[i]));
                        this.viewOpenContractsAccounts.push(false);
                    }
                    this.totalPending = this.pendingContracts.length;
                    this.totalOpen = this.openContracts.length;
                    this.totalClosed = this.closedContracts.length;

                    this.sortData('serviceName', 'asc');
                }
                this.refreshView();
            });
    }

    goto(url: string): void {
        this.generalService.closeSidenav();
        this.router.navigate([url]);
    }

    isCancellendByBuyer(status: ContractStatus): boolean {
        return (status == ContractStatus.DECLINED_BY_BUYER);
    }

    isDeclinedBySeller(status: ContractStatus): boolean {
        return (status == ContractStatus.DECLINED_BY_SELLER);
    }

    isDeclinedVerificationTimout(status: ContractStatus): boolean {
        return (status == ContractStatus.DECLINED_VERIFICATION_TIMEOUT);
    }

    isViewContractActive(status: ContractStatus): boolean {
        return !(this.isCancellendByBuyer(status) || this.isDeclinedBySeller(status) || this.isDeclinedVerificationTimout(status));
    }

    tabChanged(tabChangeEvent: MatTabChangeEvent): void {
        this.editModePos = -1;
        this.tabIndex = tabChangeEvent.index;
        this.sortData('serviceName', 'asc');
        this.refreshView();
    }

    sortDataEvent(sort: Sort) {
        this.sortData(sort.active, sort.direction);
        this.refreshView();
    }

    sortData(currentSortField: string, currentSortDirection: string) {
        switch (this.tabIndex) {
            case 0:
                this.pendingContractsSorted = [...this.sortContractData(this.pendingContracts, currentSortField, currentSortDirection)];
                break;
            case 1:
                this.openContractsSorted = [...this.sortContractData(this.openContracts, currentSortField, currentSortDirection)];
                break;
            case 2:
                this.closedContractsSorted = [...this.sortContractData(this.closedContracts, currentSortField, currentSortDirection)];
                break;
        }
    }

    sortContractData(contract: Pair[], currentSortField: string, currentSortDirection: string) {
        var sortedData = contract.sort((a, b) => {
            const isAsc = currentSortDirection === 'asc';

            switch (currentSortField) {
                case 'SupplierName':
                    return this.compare(a.value.supplierName, b.value.supplierName, isAsc);
                    break;

                case 'ServiceStart':
                    return this.compare(a.value.termFrom, b.value.termFrom, isAsc);
                    break;

                case 'ServiceEnd':
                    return this.compare(a.value.termTo, b.value.termTo, isAsc);
                    break;

                default:
                    return 0;
                    break;
            }

        });

        return sortedData;
    }

    getNameForAll(): string {
        if (this.user.isAdvisor) {
            let client = this.generalService.getStoredClient();
            if (client != null) {
                let res = "All " + client.firstName + " " + client.lastName;
                if (client.company != null) {
                    res += " (" + client.company.name + ")";
                }
                res += "'s price plans";
                return res;
            }
        }
        return "All price plans";
    }

    getUserCompanyName(company: Company | null): string {
        if (this.user.isAdvisor) {
            let client = this.generalService.getStoredClient();
            if (client != null) {
                return (company == null) ? (client.firstName + " " + client.lastName) : company.name;
            }
        }
        return (company == null) ? (this.user.firstName + " " + this.user.lastName) : company.name;
    }

    compare(a: number | string, b: number | string, isAsc: boolean) {
        return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }
}
